import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import EventBusyRounded from "@mui/icons-material/EventBusyRounded";
import GroupAddRounded from "@mui/icons-material/GroupAddRounded";
import PauseCircleOutlineRounded from "@mui/icons-material/PauseCircleOutlineRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import { Box } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { type FC, type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useRevealWage } from "@/hooks/useRevealWage";
import { MOTION } from "@/theme/tokens";
import { NamedGroupCard } from "@/ui/molecules/NamedGroupCard";
import SystemDialog, { type DialogCheckedState } from "@/ui/Popups/Dialog";
import { getShiftIndexForWindow } from "@/utils/shiftOptionWindows";
import { DIFF_HOURS_WARNING } from "@/utils/warningDialogs/utils";
import { formatShiftDate, formatShiftHours } from "./addStaffUtils";
import { MultiAddReviewHero } from "./MultiAddReviewHero";
import { buildReviewGroups, type ReviewGroup, type ReviewGroupKind } from "./multiAddReviewGroups";
import type { PendingMultiAdd } from "./useMultiAddSubmit";

export type MultiAddReviewDialogProps = {
	/** The add waiting on the review; `null` = closed. */
	pending: PendingMultiAdd | null;
	/** False where the panel has no location picker (hospital), so the hero doesn't claim one. */
	showLocation: boolean;
	isSubmitting: boolean;
	onBack: () => void;
	/** `dismissTargetWarning` = the "don't show this again" box under an over-target card was ticked. */
	onConfirm: (dismissTargetWarning: boolean) => void;
};

const DISMISS_TARGET_CHECKBOX_ID = "dismiss-staffing-target-warning";
/** The cards rise this far (px) as they fade in — a settle, not a slide. */
const GROUP_ENTER_OFFSET_PX = 6;

const GROUP_ICONS: Record<ReviewGroupKind, ReactNode> = {
	target: <GroupAddRounded />,
	employee_status: <PauseCircleOutlineRounded />,
	pto: <EventBusyRounded />,
	long_day: <ScheduleRounded />,
	overtime: <TrendingUpRounded />,
	clear: <CheckCircleOutlineRounded />,
};

/**
 * The multi-add's ONE confirmation: the hero says what's being added and where, then one card per
 * warning that applies ("On PTO (1)", "Overtime (2)", "Over the staffing target") and the staff with no
 * warnings last. "Back" keeps the picks; "Add {n} to shift" publishes all of them in one go.
 */
export const MultiAddReviewDialog: FC<MultiAddReviewDialogProps> = ({ pending, showLocation, isSubmitting, onBack, onConfirm }) => {
	const { t } = useTranslation();
	// Hold the last review while the dialog fades out, so the body doesn't blank mid-exit.
	const [shown, setShown] = useState(pending);
	if (pending && pending !== shown) setShown(pending);
	if (!shown) return null;

	const count = shown.request.nurses.length;
	const handleConfirm = (checked?: DialogCheckedState[]) => onConfirm(!!checked?.find((state) => state.id === DISMISS_TARGET_CHECKBOX_ID)?.checked);

	return (
		<SystemDialog
			dataTestId="add-staff-review-dialog"
			open={pending !== null}
			onClose={onBack}
			title={t("add.staff.review.title", { count })}
			body={<ReviewBody pending={shown} showLocation={showLocation} />}
			cancel={t("add.staff.review.back")}
			confirm={t("add.staff.confirm.count", { count })}
			onConfirm={handleConfirm}
			confirmButtonVariant="primary"
			cancelButtonVariant="secondary"
			confirmLoading={isSubmitting}
			checkboxes={
				shown.review.target
					? [{ id: DISMISS_TARGET_CHECKBOX_ID, label: t("staffing.target.warning.dont.show.again"), required: false, defaultValue: false }]
					: []
			}
			maxWidth="sm"
			fullWidth
		/>
	);
};

const ReviewBody: FC<{ pending: PendingMultiAdd; showLocation: boolean }> = ({ pending, showLocation }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const canViewWage = useRevealWage();
	const { request, review } = pending;
	// DEFAULT: the same long-day limit the review itself was built with (useNurseWarnings' fallback).
	const longShiftLimitHours = chosenDepartment.longShiftDuration ?? DIFF_HOURS_WARNING;
	const groups = buildReviewGroups(review, t, { canViewWage, longShiftLimitHours });

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<MultiAddReviewHero
				count={request.nurses.length}
				date={formatShiftDate(request.staffingDate)}
				hours={formatShiftHours(request.window)}
				shiftIndex={getShiftIndexForWindow(request.window)}
				// DEFAULT: `""` is the real "no location" pick — named "No assignment", as the footer summary does.
				location={showLocation ? request.room || t("add.staff.no.location") : null}
			/>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }} data-testid="add-staff-review-groups">
				{groups.map((group, index) => (
					<ReviewGroupCard key={group.kind} group={group} index={index} />
				))}
			</Box>
		</Box>
	);
};

const ReviewGroupCard: FC<{ group: ReviewGroup; index: number }> = ({ group, index }) => {
	const { t } = useTranslation();
	const reduceMotion = useReducedMotion();
	return (
		<motion.div
			initial={reduceMotion ? false : { opacity: 0, transform: `translateY(${GROUP_ENTER_OFFSET_PX}px)` }}
			animate={{ opacity: 1, transform: "translateY(0px)" }}
			transition={{
				duration: MOTION.DURATION.ENTRANCE_MD / 1000,
				ease: MOTION.EASE_OUT_POINTS,
				delay: Math.min(index * MOTION.STAGGER.STEP, MOTION.STAGGER.MAX) / 1000,
			}}
		>
			<NamedGroupCard
				title={group.title}
				count={group.count}
				tone={group.tone}
				icon={GROUP_ICONS[group.kind]}
				description={group.description}
				sections={[{ id: "all", items: group.items }]}
				layout={group.layout}
				showLabel={(count) => t("add.staff.review.show", { count })}
				hideLabel={t("add.staff.review.hide")}
				dataTestId={`add-staff-review-group-${group.kind}`}
			/>
		</motion.div>
	);
};
